import { faPlus, faSave } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm, useFieldArray, Controller, useWatch } from "react-hook-form";
import { useMemo } from "react";
import {
  FeeType,
  LightService,
  PlatformUser,
  AVAILABLE_USER_PLATFORM_ROLE,
} from "../../../components/users/list-users/list-users";

const capitalize = (str: string): string => {
  return str
    ? str
        .toLowerCase()
        .trim()
        .split(/\s+/)
        .map((word) => word[0].toUpperCase() + word.slice(1))
        .join(" ")
    : "";
};

type Props = {
  confirm?: () => void;
  closeFunction?: () => void;
  customParams: {
    user: PlatformUser;
    services: LightService[];
    doUpdateUser: (id: string, user: PlatformUser) => Promise<unknown>;
    isAdmin?: boolean;
  };
};

type FormValues = {
  userData: {
    first_name: string;
    last_name: string;
    email: string;
    username: string;
    role: AVAILABLE_USER_PLATFORM_ROLE | string;
    phone?: string;
    note?: string;
    isActive: boolean;
    isVerified: boolean;
  };
  data: FeeType[];
};

const UserFeeInfoContent = ({
  closeFunction,
  confirm,
  customParams: { user, services, doUpdateUser, isAdmin },
}: Props) => {
  const { control, handleSubmit, watch } = useForm<FormValues>({
    mode: "onBlur",
    defaultValues: {
      userData: {
        first_name: user.first_name ?? "",
        last_name: user.last_name ?? "",
        email: user.email ?? "",
        username: user.username ?? "",
        role: (user.role as any) ?? AVAILABLE_USER_PLATFORM_ROLE.SALES,
        phone: (user as any).phone ?? "",
        note: (user as any).note ?? "",
      },
      data: user.servicesCompensations,
    },
  });

  // ======== FEE: NON TOCCATO ========
  const { fields, append, remove } = useFieldArray<FeeType>({
    control,
    name: "data",
  });

  const dataValues = useWatch({
    control,
    name: "data",
  }) as FeeType[];

  const selectedIdsSet = useMemo(() => {
    const ids = (dataValues || [])
      .map((d: FeeType) => d?.serviceId)
      .filter((v) => !!v);
    return new Set(ids);
  }, [dataValues]);

  const hasInvalid = (watch().data || []).some(
    (item) =>
      !item?.serviceId ||
      item?.percentage === "" ||
      item?.percentage === null ||
      item?.percentage === undefined,
  );
  // ======== /FEE ========

  const onSubmitFees = async () => {
    await handleSubmit(async ({ data, userData }) => {
      // ======== FEE: NON TOCCATO ========
      const values = (data || []).map((x) => {
        return {
          serviceId: x.serviceId,
          percentage: x.percentage.toString(),
        } as FeeType;
      });
      // ======== /FEE ========

      const updatedUser: PlatformUser = {
        ...user,
        first_name: capitalize(userData.first_name),
        last_name: capitalize(userData.last_name),
        email: userData.email.trim().toLowerCase(),
        role: userData.role as any,
        servicesCompensations: values,
        ...(userData.phone !== undefined ? { phone: userData.phone } : {}),
        ...(userData.note !== undefined ? { note: userData.note } : {}),
      };

      await doUpdateUser(user._id, updatedUser);

      confirm?.();
      if (closeFunction) closeFunction();
    })();
  };

  return (
    <div className="modal-body">
      <div className="row">
        {/* ======== ALTRI CAMPI (EDITABILI) ======== */}
        <div className="col-12 col-md-6">
          <label htmlFor="first_name">Nome</label>
          <Controller
            name={"userData.first_name" as any}
            control={control}
            defaultValue={user.first_name ?? ""}
            rules={{ required: true }}
            render={({ value, onChange }) => (
              <input
                name="first_name"
                className="form-control"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
        </div>

        <div className="col-12 col-md-6">
          <label htmlFor="last_name">Cognome</label>
          <Controller
            name={"userData.last_name" as any}
            control={control}
            defaultValue={user.last_name ?? ""}
            rules={{ required: true }}
            render={({ value, onChange }) => (
              <input
                name="last_name"
                className="form-control"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
        </div>

        <div className="col-12 col-md-6 mt-3">
          <label htmlFor="email">Email</label>
          <Controller
            name={"userData.email" as any}
            control={control}
            defaultValue={user.email ?? ""}
            rules={{
              required: true,
              pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            }}
            render={({ value, onChange }) => (
              <input
                name="email"
                className="form-control"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
        </div>

        <div className="col-12 col-md-6 mt-3">
          <label htmlFor="username">Username</label>
          <Controller
            name={"userData.username" as any}
            control={control}
            defaultValue={user.username ?? ""}
            rules={{ required: true, minLength: 3 }}
            render={({ value, onChange }) => (
              <input
                disabled
                name="username"
                className="form-control"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
        </div>

        <div className="col-12 col-md-6 mt-3">
          <label htmlFor="role">Ruolo</label>
          <Controller
            name={"userData.role" as any}
            control={control}
            defaultValue={
              (user.role as any) ?? AVAILABLE_USER_PLATFORM_ROLE.SALES
            }
            rules={{ required: true }}
            render={({ value, onChange }) => (
              <select
                name="role"
                className="form-select"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
              >
                {Object.values(AVAILABLE_USER_PLATFORM_ROLE).map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}
          />
        </div>

        <div className="col-12 col-md-6 mt-3">
          <label htmlFor="phone">Telefono</label>
          <Controller
            name={"userData.phone" as any}
            control={control}
            defaultValue={(user as any).phone ?? ""}
            render={({ value, onChange }) => (
              <input
                name="phone"
                className="form-control"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
              />
            )}
          />
        </div>
        {(isAdmin as boolean) && (
          <>
            <div className="col-12 mt-3">
              <label htmlFor="note">Note</label>
              <Controller
                name={"userData.note" as any}
                control={control}
                defaultValue={(user as any).note ?? ""}
                render={({ value, onChange }) => (
                  <textarea
                    name="note"
                    className="form-control"
                    rows={3}
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                  />
                )}
              />
            </div>
          </>
        )}

        <div className="col-12 mt-3">
          <hr />
        </div>

        {/* ======== FEE (LASCIATA IDENTICA) ======== */}
        <div className="col-12">
          <div className="d-flex gap-3 flex-column">
            <span>
              <strong>Fee</strong>
              {" (indica la percentuale)"}
            </span>
            {fields &&
              fields.map((field, index) => {
                const currentServiceId = dataValues?.[index]?.serviceId;
                const availableServices = (services || []).filter(
                  (s) => !selectedIdsSet.has(s.id) || s.id === currentServiceId,
                );
                return (
                  <div key={field.id} className="d-flex gap-3">
                    <div className="d-flex gap-3" style={{ flex: "1" }}>
                      <div className="col-6">
                        <Controller
                          name={`data.${index}.serviceId` as string}
                          control={control}
                          defaultValue={(field as any).serviceId ?? ""}
                          render={({ value, onChange }) => (
                            <select
                              className="form-select"
                              value={value || ""}
                              onChange={(e) => onChange(e.target.value)}
                            >
                              <option value="">Seleziona servizio</option>
                              {availableServices.map((s) => (
                                <option key={s.id} value={s.id}>
                                  {s.name}
                                </option>
                              ))}
                            </select>
                          )}
                        />
                      </div>
                      <div className="col-6">
                        <Controller
                          name={`data.${index}.percentage` as string}
                          control={control}
                          defaultValue={(field as any).percentage ?? ""}
                          render={({ value, onChange }) => (
                            <input
                              className="form-control"
                              type="number"
                              min={0}
                              max={100}
                              step={0.01}
                              placeholder="%"
                              value={value ?? ""}
                              onChange={(e) => {
                                const val = e.target.value;
                                if (val === "") {
                                  return onChange("");
                                }
                                const castedVal = Number(val);
                                onChange(castedVal > 100 ? 100 : castedVal);
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                    <button
                      className="btn btn-danger ml-4"
                      onClick={() => {
                        remove(index);
                      }}
                    >
                      X
                    </button>
                  </div>
                );
              })}

            <div className="d-flex justify-content-end my-3">
              <button
                disabled={dataValues?.length === services.length}
                className="btn btn-primary"
                onClick={() => {
                  append({ serviceId: "", percentage: "" });
                }}
              >
                <FontAwesomeIcon icon={faPlus} />
              </button>
            </div>
          </div>
          <button
            className="col-12 mt-3 d-flex gap-2 btn btn-primary align-items-center justify-content-center"
            onClick={onSubmitFees}
            disabled={hasInvalid}
          >
            <FontAwesomeIcon icon={faSave} />
            Aggiorna Utente
          </button>
          <hr />
        </div>
      </div>
    </div>
  );
};

export default UserFeeInfoContent;
